import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Nfc, QrCode, Users, Sparkles, Contact, Signature, ArrowRight, Zap } from "lucide-react";
import Logo from "@/components/Logo";
import PricingTiers from "@/components/PricingTiers";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const IMG = {
  hero: "https://images.unsplash.com/photo-1608501821300-4f99e58bba77?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBuYXZ5JTIwdGV4dHVyZXxlbnwwfHx8fDE3ODU2MTUwODN8MA&ixlib=rb-4.1.0&q=85",
  nfc: "https://images.unsplash.com/photo-1726607962647-84ec2451d553?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHxkaWdpdGFsJTIwYnVzaW5lc3MlMjBjYXJkJTIwcGhvbmV8ZW58MHx8fHwxNzg1NjE1MDc4fDA&ixlib=rb-4.1.0&q=85",
  pros: "https://images.unsplash.com/photo-1608377733914-8e5570f5dc36?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBidXNpbmVzcyUyMHByb2Zlc3Npb25hbHN8ZW58MHx8fHwxNzg1NjE1MDczfDA&ixlib=rb-4.1.0&q=85",
};

const features = [
  { icon: QrCode, title: "QR & Vector Codes", desc: "Generate high-resolution QR codes for print and screen. Update your details live, no reprinting." },
  { icon: Nfc, title: "NFC Smart Tags", desc: "Program physical NFC cards and badges that open your digital card with a single tap." },
  { icon: Users, title: "Client Accounts & Seats", desc: "Allocate seats across teams, enforce brand identity, and share self-registration links." },
  { icon: Sparkles, title: "AI Auto-Fill & Copywriter", desc: "Paste a website URL — Gemini AI builds the card and writes a polished bio for you." },
  { icon: Contact, title: "Lead Capture Engine", desc: "Turn every card into a lead magnet. Collect prospects and export straight to CSV." },
  { icon: Signature, title: "Email Signatures", desc: "Generate copy-paste HTML signatures for Gmail & Outlook that match your brand." },
];

export default function Landing() {
  const { user } = useAuth();
  const [visits, setVisits] = useState(null);
  useEffect(() => {
    api.post("/public/visits").then((r) => setVisits(r.data.count)).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen relative overflow-x-hidden">
      <header className="glass fixed top-0 inset-x-0 z-50">
        <div className="max-w-7xl mx-auto px-5 py-4 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-8 text-sm text-[#8B9BB4]">
            <a href="#features" className="hover:text-[#F6F1E4] transition-colors">Features</a>
            <a href="#pricing" className="hover:text-[#F6F1E4] transition-colors">Pricing</a>
            <Link to="/pricing" className="hover:text-[#F6F1E4] transition-colors">Plans</Link>
          </nav>
          <div className="flex items-center gap-3">
            {user ? <Link to="/app" className="btn-gold px-5 py-2.5 rounded-full text-sm" data-testid="nav-dashboard">Dashboard</Link>
              : <><Link to="/login" className="text-sm text-[#F6F1E4] hover:text-[#D4AF37] transition-colors" data-testid="nav-login">Sign in</Link>
                <Link to="/register" className="btn-gold px-5 py-2.5 rounded-full text-sm" data-testid="nav-register">Get Started</Link></>}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-40 pb-24 px-5 iw-noise">
        <img src={IMG.hero} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(8,20,38,0.6), #081426)" }} />
        <div className="max-w-7xl mx-auto relative z-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="overline flex items-center gap-2"><Zap size={14} /> Contactless Networking Platform</motion.p>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-display text-5xl md:text-6xl font-bold leading-tight mt-4">
              Your business card,<br /><span className="text-[#D4AF37]">reimagined digitally.</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-lg text-[#8B9BB4] mt-6 leading-relaxed max-w-xl">
              iWurks360 replaces paper cards with interactive, always-up-to-date digital cards — with QR, NFC, lead capture, white-label branding and AI, across every device and OS.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="flex flex-wrap gap-4 mt-8">
              <Link to="/register" className="btn-gold px-7 py-3.5 rounded-full flex items-center gap-2" data-testid="hero-cta">Build Your Card Free <ArrowRight size={18} /></Link>
              <Link to="/pricing" className="btn-ghost px-7 py-3.5 rounded-full">View Pricing</Link>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }} className="relative">
            <img src={IMG.nfc} alt="NFC digital card" className="rounded-2xl border border-[#D4AF37]/30 shadow-2xl w-full object-cover aspect-[4/3]" />
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-5 py-24">
        <p className="overline text-center">Everything you need</p>
        <h2 className="font-display text-4xl font-bold text-center mt-2 mb-14">A complete smart card ecosystem</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="iw-card p-8" data-testid={`feature-${i}`}>
              <div className="h-12 w-12 rounded-lg bg-[#142B5A] flex items-center justify-center mb-4"><f.icon className="text-[#D4AF37]" strokeWidth={1.5} /></div>
              <h3 className="font-display text-xl font-bold">{f.title}</h3>
              <p className="text-sm text-[#8B9BB4] mt-2 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Split showcase */}
      <section className="max-w-7xl mx-auto px-5 py-12 grid lg:grid-cols-2 gap-10 items-center">
        <img src={IMG.pros} alt="Enterprise professionals" className="rounded-2xl border border-[#ffffff1a] object-cover aspect-[5/4] w-full" />
        <div>
          <p className="overline">Enterprise ready</p>
          <h2 className="font-display text-4xl font-bold mt-2">Brand governance at scale</h2>
          <p className="text-[#8B9BB4] mt-4 leading-relaxed">Roll out to hundreds of employees under one client account. Enforce logos and brand colors, allocate seats, capture leads centrally, and serve cards under your own custom domain — fully white-labeled.</p>
          <Link to="/pricing" className="btn-gold inline-flex items-center gap-2 px-6 py-3 rounded-full mt-6">Explore Enterprise <ArrowRight size={18} /></Link>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-5 py-24">
        <p className="overline text-center">Simple pricing</p>
        <h2 className="font-display text-4xl font-bold text-center mt-2 mb-14">Choose your plan</h2>
        <PricingTiers />
      </section>

      <footer className="border-t border-[#ffffff1a] py-10 px-5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Logo />
          <p className="text-sm text-[#8B9BB4]">© {new Date().getFullYear()} iWurks360DigitAL — Smart Card Platform</p>
          {visits !== null && (
            <p className="text-sm text-[#8B9BB4]" data-testid="visitor-counter">Visitors: <span className="text-[#D4AF37] font-semibold">{visits.toLocaleString()}</span></p>
          )}
        </div>
      </footer>
    </div>
  );
}
